import React, { ChangeEvent, Component, MouseEvent } from "react";
import { action, observable } from "mobx";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { delay } from "@formant/core/common/delay";
import { emptyStringToUndefined } from "@formant/core/common/emptyStringToUndefined";
import { formatUserName } from "@formant/core/common/formatUserName";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { IAnnotation } from "@formant/core/model/IAnnotation";
import { ITaggedUsers } from "@formant/core/model/ITaggedUsers";
import { IValidation } from "@formant/core/model/IValidation";
import { Uuid } from "@formant/core/model/Uuid";
import { Fader } from "@formant/ui/component/common/Fader";
import { InputElement } from "@formant/ui/component/common/Field";
import { Icon } from "@formant/ui/component/common/Icon";

import { track } from "../../analytics";
import {
    annotationStore,
    annotationTemplateStore,
    appStore,
    deviceDetailsStore,
    eventStore,
    observeStore,
    routerStore,
    userStore,
} from "../../stores";
import { localSettingObservable } from "../../utils/localSettingObservable";
import { Avatar } from "../common/Avatar";
import { Chip } from "../common/Chip";
import { DateTimeField } from "../common/DateTimeField";
import { Form } from "../common/Form";
import { Heading2 } from "../common/Heading2";
import { IconButton } from "../common/IconButton";
import { Menu } from "../common/Menu";
import { IMenuItem } from "../common/Menu/IMenuItem";
import { NoDataMessage } from "../common/NoDataMessage";
import { Scrollable } from "../common/Scrollable";
import { StandardButton } from "../common/StandardButton";
import { TextareaField } from "../common/TextareaField";
import { TextButton } from "../common/TextButton";
import { ItemList } from "../ItemList";
import { IItem } from "../ItemList/IItem";
import * as styles from "./AnnotationForm.scss";
import { ButtonWithMenu } from "./ButtonWithMenu";
import { EditAnnotationFields } from "./EditAnnotationFields";
import { EditUserAnnotationField } from "./EditUserAnnotationField";

export type SaveMode = "save" | "save-close" | "save-new";

export interface IAnnotationFormProps {
    annotation: IAnnotation;
}

@observer
export class AnnotationForm extends Component<IAnnotationFormProps> {
    @localSettingObservable("save-mode")
    private saveMode: SaveMode = "save";
    @observable
    private loading: boolean = false;
    @observable
    private validation: IValidation = {};
    @observable
    private templateMenuVisible: boolean = false;
    @observable
    private checkedTemplateId?: Uuid = undefined;
    @localSettingObservable("last-annotation-template-id")
    private lastAnnotationTemplateId?: Uuid;

    public render() {
        const {
            onChange,
            onChangeNote,
            onChangeStartTime,
            onChangeEndTime,
            onRemoveEndTime,
            onAddEndTime,
            onSelectAnnotationTemplate,
            onToggleAnnotationTemplate,
            onSelectSaveMode,
            onSubmit,
            saveMode,
            onChangeTaggedUsers,
            onClose,
            loading,
            validation,
            templateMenuVisible,
            checkedTemplateId,
            lastAnnotationTemplateId,
            onToggleTemplateMenu,
            onClearTemplate,
            onClearTemplateMouseDown,
            onLoading,
            onCancel,
        } = this;
        const { routeTo } = routerStore;
        const { annotation } = this.props;
        const {
            annotationTemplateId,
            deviceId,
            time,
            endTime,
            note,
            taggedUsers,
        } = annotation;

        const template =
            annotationTemplateId &&
            annotationTemplateStore.get(annotationTemplateId);
        const templateName = template && template.name;
        const device = deviceId && deviceDetailsStore.get(deviceId);
        const templates =
            (device
                ? annotationTemplateStore.listMatching(device.tags)
                : annotationTemplateStore.list()) || []; // TODO filter annotation templates by group tags?
        const templateMenuEnabled = template && templates.length > 1;

        // Cases when the annotation template can be automatically selected:
        // 1. There is a previous template selection compatible with this device
        // 2. There is only 1 template available for this device
        if (!annotationTemplateId) {
            let templateId: Uuid | undefined;
            if (
                lastAnnotationTemplateId &&
                templates.some(_ => _.id === lastAnnotationTemplateId)
            ) {
                templateId = lastAnnotationTemplateId;
            } else if (templates.length === 1) {
                templateId = defined(templates[0].id);
            }
            if (templateId) {
                setTimeout(async () => {
                    await onToggleAnnotationTemplate("", templateId);
                }, 500);
            }
        }

        // Menu revealed by clicking form header
        const annotationTemplateMenuItems: IMenuItem<Uuid>[] = templates.map(
            _ => ({
                value: defined(_.id),
                label: _.name,
                selected: _.id === annotationTemplateId,
                onSelected: onSelectAnnotationTemplate,
            }),
        );

        // Template list displayed when no template is selected
        const annotationTemplateListItems: IItem[] = templates.map(_ => ({
            key: defined(_.id),
            icon: "annotation",
            iconClassName: styles.icon,
            label: _.name,
            labelClassName: styles.label,
            onToggle: onToggleAnnotationTemplate,
            checked: _.id === checkedTemplateId,
            buttonClassName: styles.button,
        }));

        // Items for menu associated with save button
        const saveMenuItems: IMenuItem<string>[] = [
            {
                value: "save",
                label: "Save",
                selected: saveMode === "save",
                onSelected: onSelectSaveMode,
            },
            {
                value: "save-close",
                label: "Save & close",
                selected: saveMode === "save-close",
                onSelected: onSelectSaveMode,
            },
            {
                value: "save-new",
                label: "Save & create new",
                selected: saveMode === "save-new",
                onSelected: onSelectSaveMode,
            },
        ];

        const buttonLabel = defined(
            defined(saveMenuItems.find(_ => _.selected)).label,
        );

        const userId = annotation.userId;
        const user = defined(userStore.get(userId) || undefined);
        const reportedByUserName = formatUserName(user);

        return (
            <Form
                className={styles["annotation-form"]}
                onSubmit={onSubmit}
                onLoading={onLoading}
            >
                <Heading2 className={styles.heading}>
                    <Chip className={styles.chip} />
                    <Icon
                        name="annotation"
                        className={styles["annotation-icon"]}
                    />
                    <TextButton
                        onClicked={onToggleTemplateMenu}
                        disabled={!templateMenuEnabled}
                        className={styles.title}
                    >
                        <div>{templateName}</div>
                        {templateMenuEnabled && (
                            <Icon
                                name="dropdown"
                                className={styles["menu-icon"]}
                            />
                        )}
                    </TextButton>
                    {template && (
                        <Avatar
                            className={styles.avatar}
                            userId={userId}
                            size="small"
                            tooltip={`Reported by ${reportedByUserName}`}
                        />
                    )}
                    <IconButton
                        className={styles.close}
                        onClick={onClose}
                        icon="chevron-right"
                    />
                    <Menu
                        title="Annotation"
                        titleExtra={
                            <TextButton
                                onClicked={onClearTemplate}
                                onMouseDown={onClearTemplateMouseDown}
                                className={styles.all}
                            >
                                View All
                            </TextButton>
                        }
                        items={annotationTemplateMenuItems}
                        visible={templateMenuVisible}
                        onClose={onToggleTemplateMenu}
                        className={styles.menu}
                    />
                </Heading2>
                <Scrollable className={styles.fields}>
                    {template && (
                        <>
                            <div className={styles.times}>
                                <Icon name="clock" className={styles.clock} />
                                <div>
                                    <DateTimeField
                                        time={parseIsoDate(time)}
                                        onTimeChanged={onChangeStartTime}
                                    />
                                    <Fader
                                        visible={!!endTime}
                                        className={styles.end}
                                    >
                                        {endTime && (
                                            <DateTimeField
                                                time={parseIsoDate(endTime)}
                                                onTimeChanged={onChangeEndTime}
                                            />
                                        )}
                                    </Fader>
                                </div>
                                {endTime && (
                                    <IconButton
                                        icon="remove"
                                        onClicked={onRemoveEndTime}
                                        className={styles.remove}
                                    />
                                )}
                                {!endTime && (
                                    <IconButton
                                        icon="add-small"
                                        onClicked={onAddEndTime}
                                        className={styles.add}
                                    />
                                )}
                            </div>
                            <EditAnnotationFields
                                className={styles.field}
                                annotationTemplate={template}
                                annotation={annotation}
                                onChange={onChange}
                                validation={validation}
                            />
                            <div className={styles.field}>
                                <TextareaField
                                    invalid={!!validation.note}
                                    placeholder="Add a note..."
                                    value={note || ""}
                                    onChange={onChangeNote}
                                    small={true}
                                />
                            </div>
                        </>
                    )}

                    {!template && !!annotationTemplateListItems.length && (
                        <ItemList
                            items={annotationTemplateListItems}
                            className={styles.templates}
                        />
                    )}

                    {!annotationTemplateListItems.length && (
                        <NoDataMessage className={styles.empty} asBanner={true}>
                            <span>
                                There are no available annotation templates.
                            </span>
                            <TextButton to={routeTo.createAnnotationTemplate()}>
                                Click here to create one.
                            </TextButton>
                            <TextButton
                                to="https://docs.formant.io/docs/annotations"
                                target="_blank"
                            >
                                Click here for more info about annotations.
                            </TextButton>
                        </NoDataMessage>
                    )}
                </Scrollable>
                <div className={styles.footer}>
                    {template && (
                        <EditUserAnnotationField
                            field={{
                                type: "user",
                                key: "Related users",
                                name: "Related users",
                                required: false,
                                parameters: {},
                            }}
                            taggedUsers={taggedUsers || {}}
                            onChange={onChangeTaggedUsers}
                            bottom={true}
                            className={styles.users}
                        />
                    )}
                    <StandardButton
                        onClicked={onCancel}
                        className={styles.cancel}
                    >
                        Cancel
                    </StandardButton>
                    {template && (
                        <ButtonWithMenu
                            primary={true}
                            loading={loading}
                            label={buttonLabel}
                            items={saveMenuItems}
                            bottom={true}
                            className={styles.save}
                            data-testid="save-button"
                        />
                    )}
                </div>
            </Form>
        );
    }

    private onChange = (annotation: IAnnotation): void => {
        observeStore.editingAnnotation = annotation;
    };

    private onChangeNote = (event: ChangeEvent<InputElement>) => {
        this.onChange({
            ...this.props.annotation,
            note: event.target.value,
        });
    };

    private onChangeStartTime = (startTime: Date): void => {
        this.onChange({
            ...this.props.annotation,
            time: startTime.toISOString(),
        });
    };

    private onChangeEndTime = (endTime: Date): void => {
        this.onChange({
            ...this.props.annotation,
            endTime: endTime.toISOString(),
        });
    };

    private onRemoveEndTime = (): void => {
        this.onChange({ ...this.props.annotation, endTime: null });
    };

    private onAddEndTime = (): void => {
        const endTime = observeStore.time.toISOString();
        const updated = { ...this.props.annotation, endTime };
        // Swap start/end if necessary to make sure start < end
        if (updated.endTime < updated.time) {
            const temp = updated.time;
            updated.time = updated.endTime;
            updated.endTime = temp;
        }
        this.onChange(updated);
    };

    @action
    private onSelectAnnotationTemplate = (annotationTemplateId: Uuid): void => {
        this.onChange({ ...this.props.annotation, annotationTemplateId });
        this.lastAnnotationTemplateId = annotationTemplateId;
    };

    private onToggleAnnotationTemplate = async (
        _: string,
        annotationTemplateId?: Uuid,
    ): Promise<void> => {
        annotationTemplateId = defined(annotationTemplateId);
        this.checkedTemplateId = annotationTemplateId;
        await delay(1000);
        this.onSelectAnnotationTemplate(annotationTemplateId);
        this.checkedTemplateId = undefined;
    };

    private onSelectSaveMode = (saveMode: string): void => {
        this.saveMode = saveMode as SaveMode;
    };

    public onChangeTaggedUsers = (taggedUsers: ITaggedUsers): void => {
        this.onChange({ ...this.props.annotation, taggedUsers });
    };

    public onClose = (event: MouseEvent): void => {
        event.preventDefault();
        observeStore.closeEventSidebar();
    };

    private onSubmit = async () => {
        this.validation = {};
        const { annotationTemplateId, tags, taggedUsers } =
            this.props.annotation;

        if (!annotationTemplateId) {
            return;
        }

        // Locally validate required tags
        const annotationTemplate =
            annotationTemplateStore.get(annotationTemplateId);
        if (!annotationTemplate) {
            return;
        }
        const { fields, name } = annotationTemplate;
        const tagValidation = fields.reduce<{
            [key: string]: string[];
        }>((a, field) => {
            const { required, type, key } = field;
            if (required) {
                if (["tag", "sheet"].includes(type) && !tags?.[key]) {
                    a[`tags.${key}`] = ["Required"];
                } else if (type === "user" && !taggedUsers?.[key]) {
                    a[`taggedUsers.${key}`] = ["Required"];
                }
            }
            return a;
        }, {});
        this.validation = tagValidation;

        if (Object.keys(this.validation).length) {
            return;
        }

        try {
            const annotation = await annotationStore.save({
                ...this.props.annotation,
                message: name,
                note:
                    emptyStringToUndefined(
                        this.props.annotation?.note?.trim() || "",
                    ) || null,
            });
            eventStore.clear(); // So new tags used in the annotation are immediately available.
            const message = this.props.annotation?.id
                ? "Annotation edited"
                : "Annotation created";
            appStore.setSnackbar(message, {
                label: "View",
                onClick: () => observeStore.viewEvent(annotation),
            });
            track(message);

            const { saveMode, onChange } = this;
            if (saveMode === "save") {
                observeStore.viewEvent(annotation);
            } else if (saveMode === "save-close") {
                observeStore.closeEventSidebar();
            } else if (saveMode === "save-new") {
                const template: IAnnotation = {
                    type: "annotation",
                    organizationId: annotation.organizationId,
                    deviceId: annotation.deviceId,
                    streamName: annotation.streamName || undefined,
                    streamType: annotation.streamType || undefined,
                    time: annotation.time,
                    userId: annotation.userId,
                    annotationTemplateId: annotation.annotationTemplateId,
                    message: annotation.message,
                    tags,
                };
                onChange(template);
            }
        } catch (error) {
            const responseError = narrowError(error, ResponseError);
            const { statusCode } = responseError;
            if (statusCode === 422) {
                this.validation = responseError.validationErrors;
            } else {
                throw error;
            }
        }
    };

    private onLoading = (loading: boolean) => {
        this.loading = loading;
    };

    private onCancel = () => {
        observeStore.editingAnnotation = undefined;
    };

    private onToggleTemplateMenu = () => {
        this.templateMenuVisible = !this.templateMenuVisible;
    };

    private onClearTemplate = () => {
        this.templateMenuVisible = false;
        this.lastAnnotationTemplateId = undefined;
        this.onChange({
            ...this.props.annotation,
            annotationTemplateId: "",
        });
    };

    private onClearTemplateMouseDown = (event: MouseEvent) => {
        event.preventDefault();
    };
}
